import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import ring from "../assets/ringa.jpeg";
import PageHero from "./about/PageHero";
import CoreFeatures from "./home/CoreFeatures";
import VisaJourney from "./home/VisaJourney";
import MvG from "./about/MvG";
import StatsCounter from "./home/StatsCounter";
import DirectorSection from "./about/Director";
import { Link } from "react-router-dom";
/* ================= VARIANTS ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.85,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 30 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ================= COMPONENT ================= */

export default function About() {
  const [activeTab, setActiveTab] = useState("efforts");

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  const tabContent = {
    efforts:
      "We simplify your global ambitions through expert visa assistance, rigorous IELTS training, and personalized interview prep. Our company works tirelessly to make your transition effortless.",
    mission:
      "Our mission is to guide students with transparency, accuracy, and commitment. We ensure the best university placements and visa success.",
    goal:
      "Our goal is to simplify international education, reduce student stress, and deliver successful study abroad journeys.",
  };

  return (
    <>
      <PageHero title="About Us" subtitle="" />

      <section ref={ref} className="bg-white py-14 md:py-24">

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-0 px-5 md:px-6 items-center"
        >

          {/* ================= LEFT IMAGE ================= */}
          <motion.div
            variants={imageVariants}
            className="flex justify-center order-1 lg:order-none"
          >
            <img
              src={ring}
              alt="Travel"
              className="w-full max-w-[480px] h-auto object-contain rounded-3xl shadow-xl"
            />
          </motion.div>

          {/* ================= RIGHT CONTENT ================= */}
          <motion.div variants={containerVariants} className="max-w-[520px] order-2">

            {/* Badge */}
            <motion.span
              variants={fadeUp}
              className="inline-block border border-[#064BA5] text-[12px] sm:text-[13px] 
                         px-3 sm:px-4 py-[5px] rounded mb-4 tracking-wide text-gray-700"
            >
              Why Choose Us
            </motion.span>

            {/* Heading */}
            <motion.h2
              variants={fadeUp}
              className="text-[28px] sm:text-[34px] md:text-[38px] lg:text-[40px] 
                         font-serif leading-[1.15] text-gray-900"
            >
              Making <span className="text-[#064BA5] font-semibold">International</span> Travel  Effortless for You.
              <br className="hidden sm:block" />
             
            </motion.h2>

            {/* Intro */}
            <motion.p
              variants={fadeUp}
              className="text-gray-600 mt-4 leading-[1.7] text-[14px] sm:text-[15px]"
            >
              Master your international move with ease. We provide expert visa processing, IELTS coaching, and comprehensive preparation, ensuring your global transition is seamless, professional, and effortless.
            </motion.p>

            {/* ================= TABS ================= */}
            <motion.div variants={fadeUp} className="mt-5 sm:mt-7">
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {["efforts", "mission", "goal"].map((tab) => (
                  <motion.button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    whileTap={{ scale: 0.95 }}
                    whileHover={{ y: -2 }}
                    className={`px-4 sm:px-5 py-[7px] sm:py-[9px] text-[13px] sm:text-[14px] 
                      rounded border transition ${
                        activeTab === tab
                          ? "bg-[#064BA5] text-white border-[#064BA5] shadow-sm"
                          : "border-[#064BA5] text-gray-700 hover:bg-blue-50"
                      }`}
                  >
                    {tab === "efforts" && "Our Efforts"}
                    {tab === "mission" && "Our Mission"}
                    {tab === "goal" && "Our Goal"}
                  </motion.button>
                ))}
              </div>

              <motion.div
                variants={fadeUp}
                className="w-full h-[2px] bg-blue-800 mt-3 rounded"
              />
            </motion.div>

            {/* ================= TAB CONTENT ================= */}
            <AnimatePresence mode="wait">
              <motion.p
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45 }}
                className="text-gray-600 mt-4 leading-[1.75] text-[14px] sm:text-[15px]"
              >
                {tabContent[activeTab]}
              </motion.p>
            </AnimatePresence>

            {/* ================= BULLETS ================= */}
            <motion.ul variants={containerVariants} className="mt-4 sm:mt-5 space-y-3">
              {[
                "Client-Centric Approach",
                "Transparency & Trust",
                "Expert Guidance",
                "Faster Processing",
              ].map((item, i) => (
                <motion.li
                  key={i}
                  variants={fadeUp}
                  className="flex items-center gap-3 text-gray-700 text-[14px] sm:text-[15px]"
                >
                  <span className="w-[9px] sm:w-[10px] h-[9px] sm:h-[10px] bg-[#064BA5] rounded-full"></span>
                  {item}
                </motion.li>
              ))}
            </motion.ul>

            {/* CTA BUTTON */}
            <Link to="/contact">
            <motion.button
              variants={fadeUp}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mt-6 sm:mt-8 bg-[#064BA5] hover:bg-blue-700 text-white 
                         px-6 sm:px-7 py-[12px] sm:py-[13px] rounded shadow-md 
                         text-[14px] sm:text-[15px] transition"
            >
              More Contact Us
            </motion.button>
              </Link>
          </motion.div>
        </motion.div>
      </section>
      <DirectorSection />
      <CoreFeatures />
      <VisaJourney />
      <StatsCounter />
      <MvG />
    </>
  );
}
